import proposalsServices from '../services/proposalsServices';
import suppliersServices from '../services/suppliersServices';
import equipmentModelsServices from '../services/equipmentModelsServices';
import supplyRulesServices from '../services/supplyRulesServices';
import {
  buildAutoOrderPlan,
  buildOrderEmailText,
  emailTextToHtml,
  isWillCallDate,
} from './equipmentOrderUtils';

// Self-contained automatic equipment ordering for a proposal: fetches the
// full proposal, suppliers, catalog and supply rules, builds the plan and
// sends one order per supplier. Used where there is no preloaded ordering
// context (e.g. low-price request approval). Never throws on partial email
// failures; the caller decides how to surface the returned summary.
//
// Returns { placed, willCall, unresolved, nothingToOrder, allCovered }.
export const placeAutoOrdersForProposal = async ({ proposalId, deliveryDates = [], autoNote }) => {
  const propResp = await proposalsServices.getProposalById(proposalId);
  const proposal = propResp.data;

  const [supResp, catResp, rulesResp] = await Promise.all([
    suppliersServices.getSuppliers(),
    equipmentModelsServices.getAll({ skip: 0, take: 10000 }),
    supplyRulesServices.getAll().catch(() => ({ data: [] })),
  ]);
  const suppliers = supResp.data?.value?.data || supResp.data?.data || [];
  const models = catResp.data?.data || [];
  const catalogSuppliers = {};
  models.forEach((m) => { if (m.suppliers) catalogSuppliers[m.name] = m.suppliers; });
  const supplyRules = (rulesResp.data || []).filter((r) => r.active !== false);

  const plan = buildAutoOrderPlan({
    proposal,
    orders: proposal?.equipmentOrders || [],
    suppliers,
    catalogSuppliers,
    supplyRules,
  });

  const summary = {
    placed: 0,
    willCall: false,
    unresolved: plan.unresolved.map((u) => u.model),
    nothingToOrder: plan.nothingToOrder,
    allCovered: plan.allCovered,
  };
  if (plan.supplierGroups.length === 0) return summary;

  const earliest = [...deliveryDates].filter(Boolean).sort()[0] || null;
  const existingOrders = proposal?.equipmentOrders || [];
  const createdBy = proposal?.createdBy;

  const newOrders = plan.supplierGroups.map((group) => {
    const supplier = group.supplier;
    const emailsToSend = [];
    if (supplier?.emails && Array.isArray(supplier.emails)) {
      emailsToSend.push(...supplier.emails);
    } else if (supplier?.email) {
      emailsToSend.push(supplier.email);
    }
    const plainText = buildOrderEmailText({
      supplierName: supplier?.name || 'Supplier',
      items: group.items,
      supplies: group.supplies || [],
      publicId: proposal?.publicId,
      deliveryDate: earliest,
      createdBy,
      autoNote: autoNote || 'This order was placed automatically.',
    });
    return {
      items: [...group.items, ...(group.supplies || [])].map((item) => ({
        type: item.type,
        make: item.make,
        model: item.model,
        qty: item.qty,
        instruction: item.instruction || '',
      })),
      emails: emailsToSend,
      instruction: '',
      content: emailTextToHtml(plainText),
    };
  });

  const response = await proposalsServices.update(proposal.id, {
    equipmentOrders: [...existingOrders, ...newOrders],
  });
  if (response.status !== 200) throw new Error('Failed to save equipment orders');

  const allOrders = response.data?.equipmentOrders || [];
  const existingIds = new Set(existingOrders.map((o) => o.id).filter(Boolean));
  const createdOrders = allOrders.filter((o) => !existingIds.has(o.id));

  for (const savedOrder of createdOrders) {
    if ((savedOrder.emails || []).length > 0) {
      try {
        await proposalsServices.sendEquipmentOrder(savedOrder.id, savedOrder.content, createdBy?.email);
        summary.placed += 1;
      } catch (emailError) {
        console.error('Failed to send auto-order email:', savedOrder.id, emailError);
      }
    }
  }
  summary.willCall = isWillCallDate(earliest);
  return summary;
};
